import { generateBooleanSchema, generateRootObjectSchema, generateStringSchema, generateArraySchema, generateObjectSchema, generateNumberSchema, generateColorSchema } from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";

export const specification = new BaseAtomSpec("Worm Chart Card", generateRootObjectSchema({
    container: generateAtomContainerSchema({
        defaultValues: {
          themeMode: 'primary',
        },
      }),
      innings: generateArraySchema(
        'Innings',
        'Innings data used by the worm chart. Use 2 entries for limited overs, up to 4 for Test.',
        generateObjectSchema('Inning', 'One inning for one team in sequence', {
          teamName: generateStringSchema('Team Name', 'Team display name'),
          teamShortName: generateStringSchema('Team Short Name', 'Team tricode/short label'),
          score: generateNumberSchema('Score', 'Runs for this inning', { defaultValue: 0 }),
          wickets: generateNumberSchema('Wickets', 'Wickets for this inning', { defaultValue: 0 }),
          overs: generateNumberSchema('Overs', 'Overs played in this inning', { defaultValue: 0 }),
          runRate: generateNumberSchema('Run Rate', 'Run rate for this inning', { defaultValue: 0 }),
          progress: generateArraySchema(
            'Runs Progress',
            'Per-over cumulative runs series for this inning',
            generateNumberSchema('Runs', 'Cumulative runs at over n', { defaultValue: 0 })
          ),
          wicketsProgress: generateArraySchema(
            'Wickets Progress',
            'Per-over cumulative wickets series for this inning',
            generateNumberSchema('Wickets', 'Cumulative wickets at over n', { defaultValue: 0 })
          ),
        })
      ),
      xAxisTicks: generateArraySchema(
        'X Axis Ticks',
        'Optional manual overs labels. Leave empty to auto-generate from innings data.',
        generateNumberSchema('Over Tick', 'Over value')
      ),
      yAxisTicks: generateArraySchema(
        'Y Axis Ticks',
        'Optional manual runs labels. Leave empty to auto-generate from innings data.',
        generateNumberSchema('Run Tick', 'Runs value')
      ),
      xAxisLabel: generateStringSchema('X Axis Label', 'Label text for X-axis', { defaultValue: 'Overs' }),
      matchStatus: generateStringSchema('Match Status', 'Derived match status (pre/live/post)'),
      pregameMessage: generateStringSchema('Pregame Message', 'Message shown in chart area before match starts', {
        defaultValue: 'Graphs will display once a team has begun batting.',
      }),
      inningsTabActiveBackgroundColor: generateColorSchema(
        'Innings Tab Active Background Color',
        'Background color for selected innings tab'
      ),
      inningsTabActiveTextColor: generateColorSchema(
        'Innings Tab Active Text Color',
        'Text color for selected innings tab'
      ),
      inningsTabInactiveTextColor: generateColorSchema(
        'Innings Tab Inactive Text Color',
        'Text color for unselected innings tab'
      ),
      team1Color: generateColorSchema('Team 1 Color', 'Line/stat-box color for the first team'),
      team2Color: generateColorSchema('Team 2 Color', 'Line/stat-box color for the second team'),
      chartBackgroundColor: generateColorSchema('Chart Background Color', 'Background color for the chart area'),
      gridColor: generateColorSchema('Grid Color', 'Horizontal grid line color', {
        defaultValue: '#CECECE',
      }),
      axisTextFontSize: generateStringSchema(
        'Axis Text Font Size',
        'Shared font size for axis ticks and X axis title',
        {
          defaultValue: '1.3rem',
        }
      ),
      axisTextFontWeight: generateStringSchema(
        'Axis Text Font Weight',
        'Shared font weight for axis ticks and X axis title',
        {
          defaultValue: '400',
        }
      ),
      axisTextColor: generateColorSchema('Axis Text Color', 'Shared text color for axis ticks and X axis title'),
      axisTextFontFamily: generateStringSchema(
        'Axis Text Font Family',
        'Shared font family for axis ticks and X axis title'
      ),
      hoverTooltipValueColor: generateColorSchema(
        'Hover Tooltip Value Text Color',
        'Shared text color for both team scores in hover tooltip',
        {
          defaultValue: '#777777',
        }
      ),
      statBox12BackgroundColor: generateColorSchema(
        'StatBox 1&2 Background Color',
        'Shared background override for statBox1 and statBox2'
      ),
      statBox12TextColor: generateColorSchema(
        'StatBox 1&2 Text Color',
        'Shared text override for statBox1 and statBox2'
      ),
      statBox34BackgroundColor: generateColorSchema(
        'StatBox 3&4 Background Color',
        'Shared background override for statBox3 and statBox4'
      ),
      statBox34TextColor: generateColorSchema(
        'StatBox 3&4 Text Color',
        'Shared text override for statBox3 and statBox4'
      ),
      legendFontSize: generateStringSchema('Legend Font Size', 'Legend font size in CSS units', {
        defaultValue: '1.3rem',
      }),
      legendFontWeight: generateStringSchema('Legend Font Weight', 'Legend font weight', {
        defaultValue: '600',
      }),
      legendFontFamily: generateStringSchema('Legend Font Family', 'Legend font family'),
      statBoxTitleFontSize: generateStringSchema('Stat Box Title Font Size', 'Font size for stat box titles', {
        defaultValue: '1.3rem',
      }),
      statBoxTitleFontWeight: generateStringSchema('Stat Box Title Font Weight', 'Font weight for stat box titles', {
        defaultValue: '600',
      }),
      statBoxValueFontSize: generateStringSchema('Stat Box Value Font Size', 'Font size for stat box values', {
        defaultValue: '2rem',
      }),
      statBoxValueFontWeight: generateStringSchema('Stat Box Value Font Weight', 'Font weight for stat box values', {
        defaultValue: '800',
      }),
      statBoxOversFontSize: generateStringSchema('Stat Box Overs Font Size', 'Font size for stat box overs text', {
        defaultValue: '1.3rem',
      }),
      statBoxOversFontWeight: generateStringSchema(
        'Stat Box Overs Font Weight',
        'Font weight for stat box overs text',
        {
          defaultValue: '600',
        }
      ),
      statBoxFontFamily: generateStringSchema(
        'Stat Box Font Family',
        'Shared font family for stat box title, value, and overs text'
      ),
      showHoverInteraction: generateBooleanSchema(
        'Show Hover Interaction',
        'Show both vertical guide line and tooltip on chart hover',
        {
          defaultValue: true,
        }
      ),
      showWicketMarkers: generateBooleanSchema('Show Wicket Markers', 'Show wicket fall markers on the polylines', {
        defaultValue: true,
      }),
      hoverGuideColor: generateColorSchema('Hover Guide Color', 'Color of the hover guide line', {
        defaultValue: '#004899',
      }),
      hoverBoxBorderColor: generateColorSchema('Hover Box Border Color', 'Color of hover tooltip border', {
        defaultValue: '#CECECE',
      }),
}));